import { useEffect, useState } from "react";
import { api, ROLE_LABELS, API } from "@/lib/api";
import { Users, CheckCircle2, XCircle, AlertTriangle, FileDown, Mail, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["consiglio", "fondatori", "tesoriere", "segretario", "tesseramento", "soci", "collaboratori", "donatore", "tutti"];
const OFFICER_ROLES = ["consiglio", "tesoriere", "segretario", "tesseramento"];
const APPROVER_ROLES = ["consiglio", "tesseramento"];

export default function Admin({ user, canViewDocs }) {
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("all");
  const [registerYear, setRegisterYear] = useState(new Date().getFullYear());
  const [sendingCf, setSendingCf] = useState(false);
  const canApprove = user?.is_admin || APPROVER_ROLES.includes(user?.role);
  const isFullAdmin = user?.is_admin || user?.role === "consiglio";
  const canDownloadDoc = canViewDocs ?? canApprove;
  const load = () => api.get("/users").then(r => setMembers(r.data));
  useEffect(() => { load(); }, []);

  const downloadRegister = () => {
    window.open(`${API}/admin/registro-soci/pdf?year=${registerYear}`, "_blank");
  };

  const requestCf = async () => {
    // Preview: how many members would receive the email
    try {
      const dry = await api.post("/admin/broadcast/request-cf", { dry_run: true });
      const count = dry.data?.would_send || 0;
      if (count === 0) { toast.info("Tutti i soci hanno già il Codice Fiscale"); return; }
      if (!window.confirm(`Mandare l'email di richiesta Codice Fiscale a ${count} soci?`)) return;
      setSendingCf(true);
      const r = await api.post("/admin/broadcast/request-cf", { dry_run: false });
      toast.success(`Email inviate a ${r.data.sent} soci`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio email");
    } finally { setSendingCf(false); }
  };

  const updateRole = async (uid, role) => {
    try { await api.patch(`/users/${uid}/role`, { role }); toast.success("Ruolo aggiornato"); load(); }
    catch { toast.error("Errore aggiornamento"); }
  };

  const deleteMember = async (m) => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE l'account di ${m.name} (${m.email})?\n\nL'azione è irreversibile.`)) return;
    try { await api.delete(`/users/${m.user_id}`); toast.success("Account eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
  };

  const approve = async (uid, action) => {
    await api.post(`/users/${uid}/approve`, { action });
    toast.success(action === "approve" ? "Socio approvato" : "Richiesta rifiutata"); load();
    if (action === "approve") {
      window.dispatchEvent(new CustomEvent("giacomo:say", { detail: { text: "Benvenuto a bordo! 🤙" } }));
    }
  };

  const filtered = members.filter(m => tab === "all" ? true : (m.status || "active") === tab);
  const pendingCount = members.filter(m => m.status === "pending").length;

  const purgeAll = async () => {
    const phrase = window.prompt(
      "⚠️ RESET TOTALE\n\nQuesta azione cancellerà TUTTI i dati (soci, news, eventi, votazioni, verbali, bilancio, documenti, donazioni) tranne il tuo account owner e le impostazioni.\n\nPer confermare, digita esattamente:\n\nRESET AMUNI"
    );
    if (phrase !== "RESET AMUNI") { toast.info("Reset annullato"); return; }
    try {
      const r = await api.post("/admin/purge-all", {}, { headers: { "X-Confirm-Purge": "YES-I-AM-SURE" } });
      toast.success(`Reset completato — ${r.data.counts?.users_deleted || 0} soci rimossi`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore reset");
    }
  };

  const isOwner = user.is_admin && user.email && members.length >= 0;

  const [showLog, setShowLog] = useState(false);
  const [docLog, setDocLog] = useState(null);
  const loadLog = async () => {
    try { const r = await api.get("/admin/document-access-log?limit=200"); setDocLog(r.data); }
    catch { toast.error("Impossibile caricare il registro"); }
  };
  const toggleLog = () => { const n = !showLog; setShowLog(n); if (n && docLog === null) loadLog(); };
  const fmtDT = (s) => { try { return new Date(s).toLocaleString("it-IT"); } catch { return s; } };
  const whichLabel = { front: "Doc. fronte", back: "Doc. retro", proof: "Prova pagamento", download: "File" };

  return (
    <div data-testid="section-admin">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><Users className="w-3 h-3" /> Gestione</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Soci & Ruoli</h1>
        <p className="text-[#6B6558] mt-2">Approva le richieste in attesa, assegna i ruoli, gestisci le tessere.</p>
      </div>

      {user.is_admin && (
        <div className="mb-4 flex justify-end">
          <button
            data-testid="purge-all-btn"
            onClick={purgeAll}
            className="text-xs px-3 py-1.5 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] text-[#991B1B] font-semibold hover:bg-[#FEE2E2] inline-flex items-center gap-1"
            title="Cancella TUTTI i dati tranne il tuo account owner"
          >
            <AlertTriangle className="w-3 h-3" /> Reset totale dati
          </button>
        </div>
      )}

      {user.is_admin && (
        <div className="mb-4 paper rounded-2xl p-4" data-testid="gdpr-access-log">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#2B3A33]" />
              <div>
                <div className="font-display font-bold text-[#2B3A33]">Registro accessi documenti (GDPR)</div>
                <div className="text-xs text-[#6B6558]">Chi ha aperto i documenti d&apos;identità dei soci, quando e da quale IP.</div>
              </div>
            </div>
            <button data-testid="toggle-access-log" onClick={toggleLog}
              className="text-xs px-3 py-1.5 rounded-lg border border-[#2B3A33] bg-white text-[#2B3A33] font-semibold hover:bg-[#F3EEE4]">
              {showLog ? "Nascondi" : "Mostra registro"}
            </button>
          </div>
          {showLog && (
            <div className="mt-4 overflow-x-auto">
              {docLog === null ? (
                <div className="text-sm text-[#6B6558] p-4">Caricamento…</div>
              ) : docLog.length === 0 ? (
                <div className="text-sm text-[#6B6558] p-4">Nessun accesso registrato finora.</div>
              ) : (
                <table className="w-full text-sm min-w-0 sm:min-w-[640px]">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest text-[#8A8272] border-b border-[#E8E2D8]">
                      <th className="p-3">Data/ora</th>
                      <th className="p-3">Chi ha aperto</th>
                      <th className="p-3">Documento di</th>
                      <th className="p-3">Tipo</th>
                      <th className="p-3">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docLog.map((l) => (
                      <tr key={l.id} data-testid={`access-log-${l.id}`} className="border-b border-[#E8E2D8]/60">
                        <td className="p-3 text-[#6B6558] text-xs whitespace-nowrap">{fmtDT(l.created_at)}</td>
                        <td className="p-3">
                          <div className="font-medium text-[#2B3A33]">{l.accessor_name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[#8A8272]">{ROLE_LABELS[l.accessor_role] || l.accessor_role}</div>
                        </td>
                        <td className="p-3 text-[#2B3A33]">{l.target_user_name || l.target_user_id || "—"}</td>
                        <td className="p-3 text-[#6B6558]">{whichLabel[l.which] || l.which}</td>
                        <td className="p-3 text-[#6B6558] font-mono text-xs">{l.ip || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Actions toolbar: Registro Soci + Richiedi CF */}
      <div className="mb-4 paper rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Anno</label>
          <select
            data-testid="register-year"
            value={registerYear}
            onChange={e => setRegisterYear(parseInt(e.target.value))}
            className="px-3 py-1.5 rounded-lg border border-[#E8E2D8] bg-white text-sm"
          >
            {Array.from({length: 5}).map((_, i) => {
              const y = new Date().getFullYear() - i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <button
            data-testid="download-register-btn"
            onClick={downloadRegister}
            className="text-xs px-3 py-1.5 rounded-lg border border-[#2B3A33] bg-[#2B3A33] text-white font-semibold hover:opacity-90 inline-flex items-center gap-1"
          >
            <FileDown className="w-3.5 h-3.5" /> Registro Soci PDF
          </button>
        </div>
        {canApprove && (
          <button
            data-testid="request-cf-btn"
            onClick={requestCf}
            disabled={sendingCf}
            className="text-xs px-3 py-1.5 rounded-lg border border-[#D97706] bg-[#FEF3C7] text-[#78350F] font-semibold hover:bg-[#FDE68A] inline-flex items-center gap-1 disabled:opacity-60"
            title="Manda una email ai soci ancora senza Codice Fiscale"
          >
            <Mail className="w-3.5 h-3.5" /> {sendingCf ? "Invio in corso…" : "Richiedi CF ai soci"}
          </button>
        )}
      </div>

      {!canApprove && canDownloadDoc && (
        <div className="mb-4 paper rounded-2xl p-4 bg-[#FEF3C7] border-l-4 border-[#D97706] text-sm text-[#78350F]">
          <strong>Sola lettura</strong> — Puoi consultare i documenti di identità dei soci ma non modificare stato o ruolo.
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {[{k:"all",l:"Tutti"},{k:"pending",l:`In attesa (${pendingCount})`},{k:"active",l:"Attivi"},{k:"rejected",l:"Rifiutati"}].map(t => (
          <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.k ? "bg-[#2B3A33] text-white" : "bg-white border border-[#E8E2D8] text-[#2B3A33]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="paper rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-0 sm:min-w-[640px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-[#8A8272] border-b border-[#E8E2D8]">
              <th className="p-4">Nome</th>
              <th className="p-4 hidden md:table-cell">Email</th>
              <th className="p-4">Tipo/Ruolo</th>
              <th className="p-4">Stato</th>
              <th className="p-4 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-[#6B6558]">Nessun socio in questa categoria.</td></tr>
            )}
            {filtered.map(m => (
              <tr key={m.user_id} data-testid={`admin-row-${m.user_id}`} className="border-b border-[#E8E2D8]/60">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {m.picture ? <img src={m.picture} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full bg-[#2B3A33] text-white flex items-center justify-center font-bold">{m.name?.charAt(0)}</div>}
                    <div>
                      <div className="font-medium text-[#2B3A33]">{m.name}</div>
                      {m.is_admin && <div className="text-[10px] uppercase tracking-widest text-[#D97706] font-bold">Admin</div>}
                      {m.phone && <div className="text-xs text-[#6B6558]">{m.phone}</div>}
                    </div>
                  </div>
                </td>
                <td className="p-4 text-[#6B6558] hidden md:table-cell">{m.email}</td>
                <td className="p-4">
                  {m.membership_type && (
                    <div className="text-[10px] uppercase tracking-widest text-[#EF3E36] font-bold mb-1">
                      {m.membership_type === "attivo" ? "Attivo (Under 36)" : "Donatore"}
                    </div>
                  )}
                  <select data-testid={`role-select-${m.user_id}`} value={m.role}
                    onChange={e => updateRole(m.user_id, e.target.value)}
                    disabled={m.user_id === user.user_id || !canApprove || (!isFullAdmin && OFFICER_ROLES.includes(m.role))}
                    className="px-3 py-1.5 rounded-lg border border-[#E8E2D8] bg-white text-sm disabled:bg-[#F5F1EA] disabled:text-[#8A8272]">
                    {ROLES.filter(r => isFullAdmin || !OFFICER_ROLES.includes(r)).map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                  </select>
                  {!isFullAdmin && OFFICER_ROLES.includes(m.role) && (
                    <div className="text-[10px] text-[#8A8272] mt-1">Ruolo direttivo — solo il Consiglio può modificarlo</div>
                  )}
                </td>
                <td className="p-4">
                  {m.status === "pending" && <span className="text-xs px-2 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold">In attesa</span>}
                  {(m.status === "active" || !m.status) && <span className="text-xs px-2 py-1 rounded-full bg-[#D1FAE5] text-[#065F46] font-semibold">Attivo</span>}
                  {m.status === "rejected" && <span className="text-xs px-2 py-1 rounded-full bg-[#FEE2E2] text-[#991B1B] font-semibold">Rifiutato</span>}
                  {canDownloadDoc && (m.id_document_file_id || m.id_document_back_file_id || m.payment_proof_file_id) && (
                    <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                      {m.id_document_file_id && <a href={`${API}/admin/members/${m.user_id}/document?which=front`} target="_blank" rel="noreferrer" data-testid={`id-doc-${m.user_id}`} className="text-[10px] uppercase tracking-widest text-[#EF3E36] font-bold hover:underline">📎 Doc. fronte</a>}
                      {m.id_document_back_file_id && <a href={`${API}/admin/members/${m.user_id}/document?which=back`} target="_blank" rel="noreferrer" data-testid={`id-doc-back-${m.user_id}`} className="text-[10px] uppercase tracking-widest text-[#EF3E36] font-bold hover:underline">📎 Doc. retro</a>}
                      {m.payment_proof_file_id && <a href={`${API}/admin/members/${m.user_id}/document?which=proof`} target="_blank" rel="noreferrer" data-testid={`pay-proof-${m.user_id}`} className="text-[10px] uppercase tracking-widest text-[#065F46] font-bold hover:underline">💶 Prova pagamento</a>}
                    </div>
                  )}
                  {m.codice_fiscale && (
                    <div className="text-[10px] text-[#6B6558] mt-1 font-mono">{m.codice_fiscale}</div>
                  )}
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  {m.status === "pending" && canApprove && (
                    <>
                      <button data-testid={`approve-${m.user_id}`} onClick={() => approve(m.user_id, "approve")} className="text-xs px-2 py-1 rounded terracotta-btn inline-flex items-center gap-1 mr-1"><CheckCircle2 className="w-3 h-3" /> Approva</button>
                      <button data-testid={`reject-${m.user_id}`} onClick={() => approve(m.user_id, "reject")} className="text-xs px-2 py-1 rounded border border-[#E8E2D8] hover:bg-[#FDF2F2] inline-flex items-center gap-1 mr-1"><XCircle className="w-3 h-3" /> Rifiuta</button>
                    </>
                  )}
                  {user.is_admin && m.user_id !== user.user_id && !m.is_admin && (
                    <button data-testid={`delete-user-${m.user_id}`} onClick={() => deleteMember(m)} className="text-xs px-2 py-1 rounded border border-[#FECACA] text-[#B91C1C] hover:bg-[#FEF2F2] inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Elimina</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
